/**
 * M8-P1 §6 — the board permissions dialog, the board share dialog, the people picker and the
 * workspace Collaborators table.
 *
 * Nothing here computes a permission: the level, the principals, the caller's own rights and
 * the per-category matrix all come from `admin.sharing.*`, which reads `can()` (P1 §1).
 */
import { useOverlayDismiss } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CategoryThumbnail, permissionLabel } from './permission-wording';
import type {
  AdminSource,
  BoardPermissionLevel,
  BoardPermissionsSummary,
  MyPermissionsGroup,
  WorkspaceMemberRow,
} from './types';

/** The eight categories monday's dialog shows, in its own order (`[cap:board-permissions-dialog]`). */
export const PERMISSION_CATEGORIES = [
  'Items',
  'Subitems',
  'General',
  'Updates',
  'Columns',
  'Groups',
  'Views',
  'Forms',
] as const;

/** monday pages the strip 6 + 2 behind a 40 x 40 arrow (`[cap:board-permissions-dialog]`). */
const CARDS_PER_PAGE = 6;

/* ═══════════════════════════════════ the board permissions dialog (§6.1) */

export function BoardPermissionsDialog(props: {
  source: AdminSource;
  boardId: string;
  onClose: () => void;
  onShare?: () => void;
}): React.ReactElement {
  const { source, boardId } = props;
  const [summary, setSummary] = useState<BoardPermissionsSummary | null>(null);
  const [levels, setLevels] = useState<BoardPermissionLevel[]>([]);
  const [mine, setMine] = useState<MyPermissionsGroup[] | null>(null);
  const [category, setCategory] = useState<string>('Items');
  const [pending, setPending] = useState<string | null>(null);
  const [showMine, setShowMine] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * **P-260.** This dialog ignored Escape altogether — the probe left it open with its backdrop
   * still intercepting every pointer event on the board behind it — and returned focus nowhere.
   */
  useOverlayDismiss({ onClose: props.onClose });

  const load = useCallback(() => {
    void source.boardSummary(boardId).then(setSummary).catch(() => undefined);
  }, [source, boardId]);

  useEffect(() => {
    void source.levels().then(setLevels);
    load();
  }, [source, load]);

  const level = pending ?? summary?.permissions ?? 'everyone';
  const chosen = levels.find((entry) => entry.value === level);
  const affected = (summary?.principals ?? []).filter((row) => row.role !== 'owner').length;

  const save = useCallback(async () => {
    if (pending === null || summary === null) return;
    try {
      await source.setBoardPermissions({ boardId, permissions: pending });
      setPending(null);
      setError(null);
      load();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, [pending, summary, source, boardId, load]);

  const rows = useMemo(() => {
    const group = mine?.find((entry) => entry.category === categoryToServer(category));
    return group?.rows ?? [];
  }, [mine, category]);

  useEffect(() => {
    void source.myBoardPermissions(boardId).then(setMine).catch(() => undefined);
  }, [source, boardId]);

  /*
   * *See your own permissions* expands a section at the bottom of a column that is already
   * scrolled: on a 1440 desktop the panel opened below the dialog's visible area (the sweep read
   * it as `overlay-covered`, its centre owned by the page behind), and on a phone — where the
   * dialog is full-screen (F9-D26) — it opened below the fold. Opening a disclosure should bring
   * it into view; `nearest` scrolls the column, never the page.
   */
  const minePanel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (showMine) minePanel.current?.scrollIntoView({ block: 'nearest' });
  }, [showMine, mine]);

  /**
   * P-040 — the card strip pages 6 + 2 behind a 40 x 40 arrow on monday
   * (`[cap:board-permissions-dialog]`, cards 150 x 155 at a 158 px pitch starting at x 427).
   *
   * P-045 (round 9s) re-measured the capture and found monday's own arrow **floating over** the
   * clipped sixth card: `mf-abp-carousel-next-page` is 40 x 40 at (1264,238) and the `Groups`
   * card is 150 x 155 at (1217,180), so the arrow owns the card's centre point — the sweep's
   * `not-clickable` is real on monday too, and copying it made `Groups` unreachable by pointer
   * on three screens. Two deliberate deviations, both recorded on the card:
   *
   *   1. the arrow is laid out **beside** the strip (a flex sibling, vertically centred in the
   *      155 px band, 10 px in from the panel's right edge — the same two numbers the capture
   *      gives), so it can never intercept a card;
   *   2. all eight cards live in **one horizontal scroller** rather than a six-card slice, which
   *      is what monday's carousel is (the capture has all eight in the DOM at a 158 px pitch).
   *      A card that is scrolled out is still reachable: the browser — and Playwright — scroll
   *      the strip to it. A slice would have left `Views`/`Forms` unreachable while page 0 shows.
   *
   * The arrow still pages by whole pages of six, and the dots still say which page is showing.
   */
  const [page, setPage] = useState(0);
  const pages = Math.ceil(PERMISSION_CATEGORIES.length / CARDS_PER_PAGE);
  const strip = useRef<HTMLDivElement>(null);

  const showPage = (next: number): void => {
    setPage(next);
    const node = strip.current;
    if (!node) return;
    const left = next * node.clientWidth;
    if (typeof node.scrollTo === 'function') node.scrollTo({ left, behavior: 'smooth' });
    else node.scrollLeft = left;
  };

  /** Keep the dots honest when the strip is scrolled by anything other than the arrow. */
  const onStripScroll = (): void => {
    const node = strip.current;
    if (!node || node.clientWidth === 0) return;
    const next = Math.min(pages - 1, Math.max(0, Math.round(node.scrollLeft / node.clientWidth)));
    setPage((current) => (current === next ? current : next));
  };

  return (
    <div
      className="ez-board-permissions"
      role="dialog"
      aria-label="Board permissions"
      data-testid="board-permissions-dialog"
    >
      <div className="ez-board-permissions__header">
        <div className="ez-board-permissions__heading">
          <h2 className="ez-board-permissions__title">Board permissions</h2>
          <div className="ez-board-permissions__board">{summary?.boardName ?? ''}</div>
        </div>
        <button
          type="button"
          className="ez-board-permissions__header-action"
          onClick={() => setShowMine((open) => !open)}
          aria-expanded={showMine}
          data-testid="board-permissions-mine"
        >
          See your own permissions
        </button>
        <button
          type="button"
          className="ez-board-permissions__close"
          aria-label="Close"
          onClick={props.onClose}
        >
          ✕
        </button>
      </div>

      <div className="ez-board-permissions__body">
        <div className="ez-board-permissions__rail">
          <label className="ez-board-permissions__level-label" htmlFor="ez-board-level">
            Choose a set of permissions for this board&apos;s members:
          </label>
          <select
            id="ez-board-level"
            className="ez-board-permissions__level"
            data-testid="board-permissions-level"
            value={level}
            disabled={summary?.viewer.canSetPermissions === false}
            onChange={(event) => setPending(event.target.value)}
          >
            {levels.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
          {chosen ? (
            <p className="ez-board-permissions__level-hint" data-testid="board-permissions-description">
              {chosen.description}
            </p>
          ) : null}
          {summary && summary.level.known === false ? (
            <p className="ez-admin-chip ez-admin-chip--deleted">
              Unrecognised permission value “{summary.permissions}” — treated as the most
              restrictive level.
            </p>
          ) : null}
          {pending !== null ? (
            <p
              className="ez-board-permissions__affected"
              role="status"
              data-testid="board-permissions-affected"
            >
              {affected} people on this board will be affected. Owners are not.
            </p>
          ) : null}

          <span className="ez-board-permissions__rail-spacer" />

          <div className="ez-board-permissions__people">
            <div className="ez-board-permissions__people-title">People on this board</div>
            <div className="ez-board-permissions__people-row">
              <span>Members</span>
              <button
                type="button"
                className="ez-board-permissions__add"
                onClick={props.onShare}
                data-testid="board-permissions-add"
              >
                + Add
              </button>
            </div>
            <div className="ez-board-permissions__people-row" data-testid="board-permissions-owners">
              <span>{(summary?.ownerCount ?? 0) === 1 ? 'Owner' : 'Owners'}</span>
              <span className="ez-board-permissions__avatars">
                {(summary?.principals ?? [])
                  .filter((principal) => principal.role === 'owner')
                  .slice(0, 4)
                  .map((principal) => (
                    <span
                      key={`${principal.kind}:${principal.id}`}
                      className="ez-board-permissions__avatar"
                      title={principal.name}
                      aria-label={principal.name}
                    >
                      {initials(principal.name)}
                    </span>
                  ))}
              </span>
            </div>
          </div>
        </div>

        <div className="ez-board-permissions__main">
          <div className="ez-board-permissions__cards" data-testid="board-permissions-cards">
            <div className="ez-board-permissions__strip" ref={strip} onScroll={onStripScroll}>
              {PERMISSION_CATEGORIES.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="ez-board-permissions__card"
                  aria-pressed={category === name}
                  onClick={() => setCategory(name)}
                  data-testid={`board-permissions-card-${name.toLowerCase()}`}
                >
                  <span className="ez-board-permissions__card-thumb">
                    <CategoryThumbnail category={name} />
                  </span>
                  <span className="ez-board-permissions__card-label">{name}</span>
                </button>
              ))}
            </div>
            {pages > 1 ? (
              <button
                type="button"
                className="ez-board-permissions__pager"
                aria-label={page === 0 ? 'Next categories' : 'Previous categories'}
                data-testid="board-permissions-pager"
                onClick={() => showPage((page + 1) % pages)}
              >
                {page === 0 ? '›' : '‹'}
              </button>
            ) : null}
          </div>

          {pages > 1 ? (
            <div className="ez-board-permissions__dots" aria-hidden="true">
              {Array.from({ length: pages }, (_, index) => (
                <span
                  key={index}
                  className={`ez-board-permissions__dot${index === page ? ' ez-board-permissions__dot--on' : ''}`}
                />
              ))}
            </div>
          ) : null}

          <h3 className="ez-board-permissions__category">{category}</h3>
          <div className="ez-board-permissions__rows" data-testid="board-permissions-rows">
            {rows.map((row) => (
              <div key={row.action} className="ez-board-permissions__row">
                <span
                  className={`ez-board-permissions__mark${row.allowed ? '' : ' ez-board-permissions__mark--off'}`}
                  aria-label={row.allowed ? 'allowed' : 'denied'}
                >
                  {row.allowed ? '✓' : '✕'}
                </span>
                <span className="ez-board-permissions__row-label">
                  {permissionLabel(row.action, category)}
                </span>
              </div>
            ))}
          </div>

          <p className="ez-board-permissions__note">
            These follow the permission set above; owners can always do everything on this board.
          </p>

          {showMine ? (
            <div
              ref={minePanel}
              className="ez-board-permissions__mine"
              data-testid="board-permissions-mine-panel"
            >
              {(mine ?? []).map((group) => (
                <div key={group.category}>
                  <h4 className="ez-board-permissions__category">{group.category}</h4>
                  <div className="ez-board-permissions__rows">
                    {group.rows.map((row) => (
                      <div key={row.action} className="ez-board-permissions__row">
                        <span
                          className={`ez-board-permissions__mark${row.allowed ? '' : ' ez-board-permissions__mark--off'}`}
                          aria-label={row.allowed ? 'allowed' : 'denied'}
                        >
                          {row.allowed ? '✓' : '✕'}
                        </span>
                        <span className="ez-board-permissions__row-label">
                          {permissionLabel(row.action, group.category)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {error ? <p role="alert">{error}</p> : null}
        </div>
      </div>

      <div className="ez-board-permissions__footer">
        <a href="/help" className="ez-board-permissions__help">
          Learn how to set board permissions
        </a>
        <span className="ez-board-permissions__footer-spacer" />
        <button type="button" className="ez-board-permissions__cancel" onClick={props.onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-board-permissions__save"
          disabled={pending === null}
          onClick={() => void save()}
          data-testid="board-permissions-save"
        >
          Save
        </button>
      </div>
    </div>
  );
}

/** The avatar-group initials the rail's Owners row draws (28 x 28, `[cap:…dialog]`). */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** monday's category names against our `can()` groups. */
function categoryToServer(category: string): string {
  switch (category) {
    case 'Subitems':
      return 'Items';
    case 'Forms':
      return 'Views';
    default:
      return category;
  }
}

/*
 * **P-241 (round 10n) — M8-P1 §6.2's `ShareBoardDialog` is gone.**
 *
 * The account shipped two share dialogs and only one was reachable: the board menu's
 * *Permissions ▸ + Add* opens `modules/boards/BoardSubscribersDialog`, which round 9q rebuilt
 * from `discovery/ui-reference/subscribers/03-board-members.{png,json}` — a capture this file's
 * version never had. Both of §6.2's guards live there already: the guest rule (F8 §5.5, a guest
 * is only ever offered on a `share` board) and the last-owner rule (`LAST_OWNER_TOOLTIP`, with
 * monday's two sentences). Nothing was moved, because nothing was missing; §6.2 is amended to
 * name the subscribers dialog as the parity target.
 */

/* ══════════════════════════════════ workspace Collaborators (P1 §6.5) */

export function WorkspaceMembers(props: {
  source: AdminSource;
  workspaceId: string;
  canManage: boolean;
}): React.ReactElement {
  const { source, workspaceId } = props;
  const [rows, setRows] = useState<WorkspaceMemberRow[]>([]);
  const load = useCallback(() => {
    void source.workspaceMembers(workspaceId).then(setRows).catch(() => setRows([]));
  }, [source, workspaceId]);
  useEffect(load, [load]);

  return (
    <div data-testid="workspace-members">
      <h3>Users</h3>
      <div className="ez-admin-table__scroll">
        <table className="ez-admin-table ez-workspace-members" data-testid="workspace-members-table">
          <colgroup>
            <col style={{ width: 437 }} />
            <col style={{ width: 349 }} />
            <col style={{ width: 175 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Name &amp; title</th>
              <th>Email</th>
              <th>Workspace role</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.kind}:${row.id}`}>
                <td>
                  {row.name}
                  {row.title ? <span className="ez-admin-chip">{row.title}</span> : null}
                  {row.kind === 'team' ? <span className="ez-admin-chip">team</span> : null}
                </td>
                <td>{row.email ?? ''}</td>
                <td>
                  {props.canManage ? (
                    <select
                      aria-label={`Workspace role for ${row.name}`}
                      value={row.role}
                      onChange={(event) =>
                        void source
                          .addWorkspaceMembers({
                            id: workspaceId,
                            userIds: [row.id],
                            role: event.target.value as 'owner' | 'member',
                          })
                          .then(load)
                      }
                    >
                      <option value="owner">Owner</option>
                      <option value="member">Member</option>
                    </select>
                  ) : (
                    <span>{row.role === 'owner' ? 'Owner' : 'Member'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
